import React, { ReactNode } from 'react';
import { useTheme, ImageListItem, ImageListItemBar, IconButton } from '@mui/material';
import { Favorite as FavoriteIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { CURRENCY_SYMBOLS, PlanListItem } from '../types';
import useAuth from './Auth';
import { FavouriteIconButton, FlashingFavouriteIconButtonWithRegisterTooltip } from './FavouriteButton';
import { PlanAuthorCaption } from '../containers/plans/PlanView';
import { useTranslation } from 'react-i18next';
import { BRAND_YELLOW } from '../ThemeProvider';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

dayjs.extend(relativeTime);

interface PlanListProps {
	children: NonNullable<ReactNode>;
	cols?: number;
	rowHeight?: number;
}

interface PlanListItemElProps {
	item: PlanListItem,
	isFavourited: boolean,
	onRemoveFavouriteBtnClick?: () => void,
	onAddFavouriteBtnClick?: () => void,
	subtitle?: ReactNode | null,
}

export const PlanListItemEl = ({ item, isFavourited, onRemoveFavouriteBtnClick, onAddFavouriteBtnClick, subtitle }: PlanListItemElProps) => {
	const theme = useTheme();
	const isUnavailable = !item.publishedAt;
	const {userProfile} = useAuth();
	const { t } = useTranslation();

	const handleAddFavouriteBtnClick = () => {
		if (!onAddFavouriteBtnClick) {
			return;
		}
		onAddFavouriteBtnClick();
	};

	const handleRemoveFavouriteBtnClick = () => {
		if (onRemoveFavouriteBtnClick) {
			onRemoveFavouriteBtnClick();
		}
	};

	const { elementRef, isIntersecting } = useIntersectionObserver({
		rootMargin: '50px', // Start loading 50px before the image comes into view
		triggerOnce: true,
	});

	const shouldShowFlashingFavouriteIconButton = !userProfile && item.materialsCost;
	const currencySymbol = CURRENCY_SYMBOLS[item.materialsCostCurrency];

	return isUnavailable ? <ImageListItem>
		<ImageListItemBar
			position='top'
			title={'Content unavailable'}
			subtitle={'The entry has been removed'}
			actionIcon={
				<IconButton
					sx={{ color: 'rgba(255, 255, 255, 0.54)', mt:1}}
					aria-label={`info about ${item.title}`}
					onClick={handleRemoveFavouriteBtnClick}
				>
					<FavoriteIcon color='primary' />
				</IconButton>
			}
		/>
	</ImageListItem> : <ImageListItem key={item.imgUrl} component={Link} to={`/plans/${item.id}`} ref={elementRef} sx={{
		cursor: 'pointer', 
		overflowX:'hidden',
		textDecoration: 'none',
		color: 'inherit',
		'& .MuiImageListItemBar-positionTop': {
			color: BRAND_YELLOW,
		},
	}}>
		<img
			srcSet={isIntersecting ? `${item.imgUrl}?w=248&fit=crop&auto=format&dpr=2 2x` : undefined}
			src={isIntersecting ? `${item.imgUrl}?w=248&fit=crop&auto=format` : undefined}
			alt={item.title}
			loading="lazy"
			style={{borderRadius: '8px', overflow: 'hidden'}}
		/>
		<ImageListItemBar
			title={item.alternativeTitle || item.title}
			subtitle={subtitle !== null ? <PlanAuthorCaption authorName={item.authorName} source={item.source} /> : null}
			actionIcon={shouldShowFlashingFavouriteIconButton ? <FlashingFavouriteIconButtonWithRegisterTooltip /> : <FavouriteIconButton 
				isFavourited={isFavourited} 
				onRemoveFavouriteBtnClick={handleRemoveFavouriteBtnClick} 
				onAddFavouriteBtnClick={handleAddFavouriteBtnClick} 
			/>}
			position='below'
		/>
		{item.materialsCost ? <ImageListItemBar
			sx={{ 
				textAlign: 'right',
				boxShadow: theme.shadows[2],
				'& .MuiImageListItemBar-titleWrap': { 
					color: 'inherit'
				}, 
				'& .MuiImageListItemBar-title': {
					fontWeight: 'bold'
				},
				borderTopLeftRadius: '8px',
				borderTopRightRadius: '8px',
			}}
			title={`${currencySymbol}${item.materialsCost}`}
			subtitle={t('discover.offerAvailable')}
			actionPosition='right'
			position='top'
		/> : <ImageListItemBar
			sx={{ 
				fontWeight: 'bold',
				textAlign: 'right', 
				'& .MuiImageListItemBar-titleWrap': { 
					color: isFavourited ? 'inherit' : 'white'
				}, 
				'& .MuiImageListItemBar-subtitle': { 
					color: isFavourited ? 'inherit' : BRAND_YELLOW
				}, 
				'& .MuiImageListItemBar-title': { 
					fontWeight: 'bold'
				}, 
				boxShadow: theme.shadows[2],
				borderTopLeftRadius: '8px',
				borderTopRightRadius: '8px',
			}}
			title={isFavourited ? t('discover.inWishlist') : t('discover.addToWishlist')}
			subtitle={isFavourited ? null : t('discover.addToWishlistDescription')}
			actionPosition='right'
			position='top'
			onClick={(e) => {
				e.preventDefault();
				if (!isFavourited) {
					handleAddFavouriteBtnClick();
				}
			}}
		/>}
	</ImageListItem>;
};


// TODO: unify or rename
interface PlanListItemElPropsV2 {
	item: PlanListItem,
	subtitle?: ReactNode | null,
}

export const PlanListItemElV2 = ({ item, subtitle }: PlanListItemElPropsV2) => {
	const theme = useTheme();

	return <ImageListItem key={item.imgUrl} component={Link} to={`/plans/${item.id}`} sx={{cursor: 'pointer', overflowX:'hidden'}}>
		<img
			srcSet={`${item.imgUrl}?w=248&fit=crop&auto=format&dpr=2 2x`}
			src={`${item.imgUrl}?w=248&fit=crop&auto=format`}
			alt={item.title}
			loading="lazy"
			style={{borderRadius: '8px', boxShadow: theme.shadows[2], overflow: 'hidden'}}
		/>
		<ImageListItemBar
			title={item.title}
			subtitle={subtitle ?? <PlanAuthorCaption authorName={item.authorName} source={item.source} />}
			position='below'
			sx={{
				'& .MuiImageListItemBar-titleWrap': { 
					color: 'text.primary',
				},
			}}
		/>
	</ImageListItem>;
};

export const PlanListItemContainer = ({ children }: PlanListProps) => {
	return <ImageListItem sx={{cursor: 'pointer', overflowX:'hidden'}}>
		{children}
	</ImageListItem>;
};
